import React, { useMemo } from 'react';
import type { Test } from '@mariana/labos-client';
import { WorksheetViewer } from '../WorksheetViewer';
import { SpreadsheetTable } from '../SpreadsheetTable';
import { formatAnalyticalValue } from '../../utils/formatValue';
import './ResultViewers.css';

interface ICPResultViewerProps {
  test: Test;
}

const ICP_ELEMENTS = [
  'Al', 'As', 'B', 'Ba', 'Be', 'Br', 'Ca', 'Cd', 'Cl', 'Co',
  'Cr', 'Cu', 'Fe', 'I', 'K', 'Li', 'Mg', 'Mn', 'Na', 'Ni',
  'P', 'Pb', 'S', 'Sb', 'Si', 'Sn', 'Sr', 'Ti', 'Zn',
];

export const ICPResultViewer: React.FC<ICPResultViewerProps> = ({ test }) => {
  const worksheetData = test.attributes?.worksheet_data as Record<string, any> | undefined;
  const rawWorksheetData = test.attributes?.raw_worksheet_data as Record<string, Record<string, any>> | undefined;

  const reportedRows = useMemo(() => {
    if (!worksheetData) return [];
    return ICP_ELEMENTS.map(el => {
      const key = `report_export_${el.toLowerCase()}`;
      const entry = worksheetData[key];
      const value = entry?.value ?? entry;
      return [el, formatAnalyticalValue(value)] as [string, string];
    });
  }, [worksheetData]);

  return (
    <div className="rv-container">
      {rawWorksheetData && (
        <div className="rv-section rv-section-raw">
          <h3 className="rv-section-title">Raw Worksheet</h3>
          <WorksheetViewer rawWorksheetData={rawWorksheetData} />
        </div>
      )}

      {worksheetData && reportedRows.length > 0 && (
        <div className="rv-section rv-section-reported">
          <h3 className="rv-section-title">Reported Values</h3>
          <SpreadsheetTable
            headers={['Element', 'Value']}
            rows={reportedRows}
            showRowNumbers={false}
          />
        </div>
      )}

      {!worksheetData && !rawWorksheetData && (
        <div className="rv-empty">No ICP results available for this test.</div>
      )}
    </div>
  );
};
